import { createPagePagingActions, SortDirection, TableQuery, useTableQuery, useTableQueryPaging, useTableQuerySort } from "@comet/admin";
import { Table } from "@comet/admin/lib/table/Table";
import { Add } from "@mui/icons-material";
import { Button, Dialog, IconButton } from "@mui/material";
import { ContentWrapper, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { type GQLActivitiesAdminQuery, type GQLActivitiesAdminQueryVariables, type GQLCameraAdminQuery } from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { type FC, type PropsWithChildren, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";

// eslint-disable-next-line @comet/no-private-sibling-import
import { activitiesQuery } from "./ActivityLog.gql";
import AddForm from "./AddForm";
import EditForm from "./EditForm";

export const DialogContent = styled.div`
    min-width: 400px;
`;

const ActivityLogTable: FC<PropsWithChildren<GQLCameraAdminQuery>> = ({ cameraAdmin }) => {
    const intl = useIntl();
    const pagingApi = useTableQueryPaging(1);
    const sortApi = useTableQuerySort({ columnName: "activity.createdAt", direction: SortDirection.DESC });
    const addAdditionalQueryVariablesFn = useCallback(() => ({ cameraId: cameraAdmin.id, size: 5 }), [cameraAdmin.id]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentId, setCurrentId] = useState<undefined | string>();

    const memoizedVariables = useMemoizedTableVariables<GQLActivitiesAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLActivitiesAdminQuery, GQLActivitiesAdminQueryVariables>()(activitiesQuery, {
        resolveTableData: ({ activitiesAdmin }) => ({
            data: activitiesAdmin.nodes,
            totalCount: activitiesAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, activitiesAdmin),
        }),
        variables: memoizedVariables,
    });

    function handleCloseDialog() {
        setDialogOpen(false);
        setCurrentId(undefined);
    }

    return (
        <>
            <Dialog open={dialogOpen}>
                <DialogContent>
                    {currentId ? (
                        <EditForm handleClose={handleCloseDialog} id={currentId} />
                    ) : (
                        <AddForm cameraAdmin={cameraAdmin} handleClose={handleCloseDialog} />
                    )}
                </DialogContent>
            </Dialog>
            <ContentWrapper
                title={intl.formatMessage({ id: "reviermanager.camera.activities", defaultMessage: "Aktivitäten" })}
                link={
                    <Button color="primary" variant="text" onClick={() => setDialogOpen(true)} startIcon={<Add />}>
                        <FormattedMessage id="reviermanager.camera.activity.add" defaultMessage="Hinzufügen" />
                    </Button>
                }
            >
                <TableQuery api={api} loading={loading} error={error}>
                    {tableData && (
                        <Table
                            {...tableData}
                            sortApi={sortApi}
                            rowName={intl.formatMessage(
                                {
                                    id: "reviermanager.camera.activities.table",
                                    defaultMessage: "{count, plural, one {Aktivität} other {Aktivitäten}}",
                                },
                                { count: tableData.totalCount },
                            )}
                            columns={[
                                {
                                    name: "activity.createdAt",
                                    sortable: true,
                                    header: <FormattedMessage id="reviermanager.camera.activity.createdAt" defaultMessage="Datum" />,
                                    render: ({ createdAt }) => <FormattedDateWidthDateTime value={createdAt} />,
                                },
                                {
                                    name: "createdBy",
                                    header: <FormattedMessage id="reviermanager.camera.activity.createdBy" defaultMessage="createdBy" />,
                                    render: ({ createdBy }) => createdBy,
                                },
                                {
                                    name: "message",
                                    header: <FormattedMessage id="reviermanager.camera.activity.messaeg" defaultMessage="Notiz" />,
                                    render: ({ message }) => message,
                                },
                                {
                                    name: "actions",
                                    render: ({ id }) => (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setCurrentId(id);
                                                setDialogOpen(true);
                                            }}
                                        >
                                            <ArrowRight color="primary" />
                                        </IconButton>
                                    ),
                                },
                            ]}
                        />
                    )}
                </TableQuery>
            </ContentWrapper>
        </>
    );
};
export default ActivityLogTable;
